import { useNavigate } from 'react-router-dom';
import type { ArticlePost } from '@/lib/articles';
import Avatar from '@/components/base/Avatar';

interface Props {
  posts: ArticlePost[];
}

const TrendingSidebar = ({ posts }: Props) => {
  const navigate = useNavigate();
  const authors = posts.reduce<Array<{ name: string; role: string; avatar: string; articles: number }>>((list, post) => {
    const existing = list.find((item) => item.name === post.author);
    if (existing) {
      existing.articles += 1;
      return list;
    }
    list.push({
      name: post.author,
      role: [post.authorRole, post.authorCompany].filter(Boolean).join(' · '),
      avatar: post.authorAvatar,
      articles: 1
    });
    return list;
  }, []).slice(0, 3);
  const tags = Array.from(new Set(posts.flatMap((post) => post.tags))).slice(0, 12);

  return (
    <aside className="w-72 flex-shrink-0">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-rose-50 rounded-lg">
            <i className="ri-fire-line text-rose-500 text-sm" />
          </div>
          <h3 className="text-sm font-bold text-[#0A1628]">本週熱讀</h3>
        </div>
        <div className="flex flex-col gap-0">
          {posts.map((post, idx) => (
            <div
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="group cursor-pointer flex items-start gap-3 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded-xl transition-colors"
            >
              <span className={`text-xl font-black leading-none mt-0.5 flex-shrink-0 ${idx === 0 ? 'text-rose-400' : idx === 1 ? 'text-orange-400' : 'text-slate-300'}`}>
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-semibold text-[#0A1628] leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors mb-1.5">
                  {post.title}
                </h4>
                <div className="flex items-center gap-2">
                  <Avatar src={post.authorAvatar} alt={post.author} className="w-4 h-4 rounded-full flex-shrink-0" />
                  <span className="text-[10px] text-slate-400 truncate">{post.author}</span>
                  <span className="text-[10px] text-slate-300 flex items-center gap-0.5 flex-shrink-0">
                    <i className="ri-eye-line" />{(post.views / 1000).toFixed(1)}K
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-violet-50 rounded-lg">
            <i className="ri-user-star-line text-violet-500 text-sm" />
          </div>
          <h3 className="text-sm font-bold text-[#0A1628]">活躍作者</h3>
        </div>
        <div className="flex flex-col gap-3">
          {authors.map((author) => (
            <div key={author.name} className="flex items-center gap-3">
              <Avatar src={author.avatar} alt={author.name} className="w-9 h-9 rounded-full flex-shrink-0 bg-slate-100" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#0A1628] truncate">{author.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{author.role || 'NexusGo'}</p>
              </div>
              <span className="text-[10px] text-slate-400 flex-shrink-0 bg-slate-50 px-2 py-0.5 rounded-full">
                {author.articles} 篇
              </span>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate('/mentors')}
          className="mt-4 w-full py-2.5 border border-slate-200 hover:border-slate-300 text-xs font-semibold text-slate-600 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
        >
          查看全部導師
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 flex items-center justify-center bg-sky-50 rounded-lg">
            <i className="ri-price-tag-3-line text-sky-500 text-sm" />
          </div>
          <h3 className="text-sm font-bold text-[#0A1628]">熱門標籤</h3>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              onClick={() => navigate(`/blog?search=${encodeURIComponent(tag)}`)}
              className="px-2.5 py-1 bg-slate-50 hover:bg-slate-100 text-slate-500 text-[11px] rounded-full cursor-pointer transition-colors whitespace-nowrap border border-slate-100"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default TrendingSidebar;
